'use client';

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import {
    Server, Activity, Wifi, WifiOff, Clock, Cpu, ArrowRight, RefreshCw,
    FolderSync, AlertTriangle, GitBranch, CheckCircle, Loader2, Circle,
    Code2, Zap, Layers, ShieldCheck, BrainCircuit, Eye, FileSearch,
    FilePlus2, FileEdit, FileX, HardDrive, FolderOpen, Timer, Bot, Brain, CheckCircle2, CircleCheck, ClipboardList, FileText, Folder} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';


/* ═══ Types ═══ */
interface NodeData {
    id: string;
    name: string;
    emoji: string;
    hw: string;
    role: string;
    ip: string;
    port: number;
    online: boolean;
    service: string;
    latency: number;
    uptime: number | null;
    memory: { heapUsed: string; heapTotal: string; rss: string } | null;
    skills?: string[];
    agentTypes?: string[];
    devPhase?: string;
    completedCount?: number;
    inProgressCount?: number;
}

interface GridStatus {
    timestamp: string;
    gridName: string;
    version: string;
    total: number;
    online: number;
    uptime_pct: number;
    nodes: NodeData[];
}

interface DevNode {
    id: string;
    name: string;
    emoji: string;
    role: string;
    status: string;
    skills: string[];
    agentTypes: string[];
    development: {
        phase: string;
        completedFeatures: string[];
        inProgress: string[];
        scripts: string[];
    };
}

interface DevStatus {
    timestamp: string;
    summary: {
        production: number;
        development: number;
        planned: number;
        totalFeatures: number;
        totalInProgress: number;
    };
    nodes: DevNode[];
}

const SERVICE_COLORS: Record<string, string> = {
    gateway: '#3fb950',
    openclaw: '#58a6ff',
    local: '#f0c000',
    offline: '#f85149',
    http: '#8b949e',
};

const PHASE_CONFIG: Record<string, { color: string; bg: string; label: string; icon: React.ReactNode }> = {
 production: { color: '#3fb950', bg: 'bg-green-500/10', label: ' 운영중', icon: <CheckCircle className="w-3.5 h-3.5" />},
 development: { color: '#58a6ff', bg: 'bg-blue-500/10', label: ' 개발중', icon: <Code2 className="w-3.5 h-3.5" />},
    planned: { color: '#8b949e', bg: 'bg-gray-500/10', label: '⬜ 계획', icon: <Circle className="w-3.5 h-3.5" /> },
 unknown: { color: '#6e7681', bg: 'bg-gray-500/5', label: ' 미정', icon: <Circle className="w-3.5 h-3.5" />},
};

const SKILL_COLORS: Record<string, string> = {
    rag: '#10b981', search: '#10b981', gateway: '#f59e0b', dslm: '#8b5cf6',
    code: '#3b82f6', 'btl-parsing': '#ec4899', 'design-automation': '#a855f7',
    cad: '#ef4444', dwg: '#ef4444', ppt: '#f97316', sketchup: '#f97316',
    comfyui: '#06b6d4', 'image-generation': '#06b6d4',
    vision: '#8b5cf6', analysis: '#8b5cf6', orchestration: '#eab308',
    'resource-management': '#84cc16', indexing: '#84cc16',
    'concept-design': '#ec4899', 'customer-support': '#64748b',
};

// T19-2: NAS Watcher types
interface WatcherStatus {
    running: boolean;
    dirs: number;
    files: number;
    events: number;
    scanCount: number;
    lastScan: string | null;
    interval: number;
}

interface FileEvent {
    type: 'created' | 'modified' | 'deleted';
    path: string;
    name: string;
    ext: string;
    size: number;
    prevSize?: number;
    timestamp: string;
}

interface WatcherConfig {
    running: boolean;
    interval: number;
    extensions: string[];
    dirs: string[];
    trackedFiles: number;
    totalEvents: number;
    scanCount: number;
    lastScan: string | null;
}

const EVENT_ICONS = {
    created: <FilePlus2 className="w-4 h-4 text-green-400" />,
    modified: <FileEdit className="w-4 h-4 text-blue-400" />,
    deleted: <FileX className="w-4 h-4 text-red-400" />,
};

const EXT_COLORS: Record<string, string> = {
    '.dwg': '#ef4444', '.dxf': '#ef4444',
    '.xlsx': '#22c55e', '.csv': '#22c55e',
    '.pdf': '#f97316', '.pptx': '#f97316',
    '.skp': '#8b5cf6',
    '.jpg': '#06b6d4', '.png': '#06b6d4',
    '.hwp': '#3b82f6', '.docx': '#3b82f6',
};

const NAS_GATEWAY = process.env.NEXT_PUBLIC_NAS_GATEWAY || 'https://grid.snapcodex.com';

export default function GridPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [grid, setGrid] = useState<GridStatus | null>(null);
    const [devData, setDevData] = useState<DevStatus | null>(null);
    const [nasInfo, setNasInfo] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState('');
    const [expandedNode, setExpandedNode] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'grid' | 'dev' | 'routing' | 'watcher'>('grid');
    // T19-2: NAS Watcher state
    const [watcherStatus, setWatcherStatus] = useState<WatcherStatus | null>(null);
    const [watcherConfig, setWatcherConfig] = useState<WatcherConfig | null>(null);
    const [fileEvents, setFileEvents] = useState<FileEvent[]>([]);
    const [eventFilter, setEventFilter] = useState<string>('all');

    const fetchData = useCallback(async () => {
        try {
            const [gridResp, devResp, nasResp] = await Promise.all([
                fetch('/api/grid'),
                fetch('/api/grid?detail=dev'),
                fetch('/api/nas/watch?action=status').catch(() => null)
            ]);
            if (gridResp.ok) setGrid(await gridResp.json());
            if (devResp.ok) setDevData(await devResp.json());
            if (nasResp?.ok) setNasInfo(await nasResp.json());

            // T19-2: Fetch NAS Gateway watcher data
            try {
                const [healthResp, eventsResp, configResp] = await Promise.all([
                    fetch(`${NAS_GATEWAY}/health`, { signal: AbortSignal.timeout(5000) }).catch(() => null),
                    fetch(`${NAS_GATEWAY}/api/watch/events?limit=30`, { signal: AbortSignal.timeout(5000) }).catch(() => null),
                    fetch(`${NAS_GATEWAY}/api/watch/config`, { signal: AbortSignal.timeout(5000) }).catch(() => null),
                ]);
                if (healthResp?.ok) {
                    const health = await healthResp.json();
                    setWatcherStatus(health.watcher);
                }
                if (eventsResp?.ok) {
                    const evData = await eventsResp.json();
                    setFileEvents(evData.events || []);
                }
                if (configResp?.ok) {
                    setWatcherConfig(await configResp.json());
                }
            } catch { /* NAS Gateway offline */ }

            setLastUpdate(new Date().toLocaleTimeString('ko-KR'));
        } catch (e) {
            console.error('Fetch error:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000); // 30초 폴링 (과금 방어)
        return () => clearInterval(interval);
    }, [fetchData]);

    const getDevNode = (id: string) => devData?.nodes.find(n => n.id === id);

    return (
        <main className={`min-h-screen pt-16 ${isDark ? 'bg-gradient-to-b from-gray-950 via-[#0a0e1a] to-gray-950 text-white' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50 text-gray-900'}`}>

            {/* Hero */}
            <section className="py-10 relative overflow-hidden">
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-green-900/20 via-blue-900/10 to-cyan-900/20' : 'bg-gradient-to-r from-green-50 via-blue-50 to-cyan-50'}`} />
                <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 mb-6 border ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                            <Server className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                            <span className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-700'}`}>9-Node Distributed Grid</span>
                        </div>
                        <h1 className={`text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent ${isDark ? 'bg-gradient-to-r from-white via-green-100 to-blue-200' : 'bg-gradient-to-r from-gray-900 via-green-700 to-blue-600'}`}>
                            Snap<span className="text-blue-500">GRID</span> Agent Registry
                        </h1>
                        <p className={`max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Distributed Body, Unified Mind — 9개 에이전트 노드 실시간 모니터링 & 개발 진행 통합 대시보드
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="max-w-6xl mx-auto px-6 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    {[
                        { label: '온라인', val: grid ? `${grid.online}/${grid.total}` : '-', color: '#3fb950', icon: <Wifi className="w-4 h-4" /> },
                        { label: '가동률', val: grid ? `${grid.uptime_pct}%` : '-', color: '#58a6ff', icon: <Activity className="w-4 h-4" /> },
                        { label: '운영', val: devData ? `${devData.summary.production}` : '-', color: '#3fb950', icon: <CheckCircle className="w-4 h-4" /> },
                        { label: '개발중', val: devData ? `${devData.summary.development}` : '-', color: '#58a6ff', icon: <Code2 className="w-4 h-4" /> },
                        { label: '완료 기능', val: devData ? `${devData.summary.totalFeatures}` : '-', color: '#f0c000', icon: <Zap className="w-4 h-4" /> },
                        { label: '진행 작업', val: devData ? `${devData.summary.totalInProgress}` : '-', color: '#a855f7', icon: <Loader2 className="w-4 h-4" /> },
                    ].map(s => (
                        <div key={s.label} className={`p-3 rounded-xl border text-center ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                            <div className="flex items-center justify-center gap-1.5 mb-1" style={{ color: s.color }}>{s.icon}</div>
                            <div className="text-xl font-bold font-mono" style={{ color: s.color }}>{s.val}</div>
                            <div className={`text-[10px] font-semibold uppercase tracking-wide mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tab Navigation */}
            <section className="max-w-6xl mx-auto px-6 mb-6">
                <div className={`flex gap-1 p-1 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                    {([
 { key: 'grid', label: ' 그리드', icon: <Server className="w-4 h-4" />},
 { key: 'dev', label: '개발', icon: <GitBranch className="w-4 h-4" />},
 { key: 'watcher', label: ` NAS 와쳐${watcherStatus?.running ? ` (${watcherStatus.files})` : ''}`, icon: <Eye className="w-4 h-4" />},
 { key: 'routing', label: '라우팅', icon: <Layers className="w-4 h-4" />},
                    ] as const).map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                                activeTab === tab.key
                                    ? isDark ? 'bg-white/10 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                                    : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                            }`}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Content */}
            <section className="max-w-6xl mx-auto px-6 pb-16">
                <AnimatePresence mode="wait">
                    {/* ═══ Grid Status Tab ═══ */}
                    {activeTab === 'grid' && (
                        <motion.div key="grid" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <Activity className="w-5 h-5 text-green-400" />
                                    <h2 className="text-xl font-bold">노드 상태</h2>
                                    {grid && <span className="text-xs bg-green-500/10 text-green-400 px-3 py-1 rounded-full font-bold animate-pulse">LIVE</span>}
                                </div>
                                <button
                                    onClick={() => { setLoading(true); fetchData(); }}
                                    className={`flex items-center gap-2 text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                                >
                                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                    {lastUpdate}
                                </button>
                            </div>

                            <div className="grid md:grid-cols-3 gap-4">
                                {grid?.nodes?.map((node, i) => {
                                    const dev = getDevNode(node.id);
                                    const phase = PHASE_CONFIG[node.devPhase || 'unknown'] || PHASE_CONFIG.unknown;
                                    const isExpanded = expandedNode === node.id;

                                    return (
                                        <motion.div
                                            key={node.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.05 }}
                                            onClick={() => setExpandedNode(isExpanded ? null : node.id)}
                                            className={`p-5 rounded-2xl border transition-all cursor-pointer ${
                                                node.online
                                                    ? isDark ? 'border-white/10 bg-white/[0.02] hover:border-white/20' : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'
                                                    : isDark ? 'border-white/5 bg-white/[0.01] opacity-50' : 'border-gray-100 bg-gray-50 opacity-50'
                                            }`}
                                        >
                                            {/* Header */}
                                            <div className="flex items-center gap-3 mb-3">
                                                <span className="text-2xl">{node.emoji}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-bold">{node.name}</h3>
                                                        <span
                                                            className="text-[10px] font-bold px-2 py-0.5 rounded"
                                                            style={{
                                                                background: `${SERVICE_COLORS[node.service] || '#8b949e'}20`,
                                                                color: SERVICE_COLORS[node.service] || '#8b949e',
                                                            }}
                                                        >
                                                            {node.service?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{node.hw}</p>
                                                </div>
                                                {node.online
                                                    ? <Wifi className="w-4 h-4 text-green-400" />
                                                    : <WifiOff className="w-4 h-4 text-red-400" />
                                                }
                                            </div>

                                            {/* Role + Phase */}
                                            <div className="flex items-center justify-between mb-2">
                                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{node.role}</p>
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${phase.bg}`} style={{ color: phase.color }}>
                                                    {phase.label}
                                                </span>
                                            </div>

                                            {/* Skills */}
                                            {node.skills && node.skills.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mb-3">
                                                    {node.skills.slice(0, isExpanded ? undefined : 4).map(skill => (
                                                        <span
                                                            key={skill}
                                                            className="text-[8px] font-bold px-1.5 py-0.5 rounded"
                                                            style={{
                                                                background: `${SKILL_COLORS[skill] || '#6e7681'}15`,
                                                                color: SKILL_COLORS[skill] || '#6e7681',
                                                            }}
                                                        >
                                                            {skill}
                                                        </span>
                                                    ))}
                                                    {!isExpanded && node.skills.length > 4 && (
                                                        <span className={`text-[8px] px-1.5 py-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                            +{node.skills.length - 4}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Stats */}
                                            <div className={`flex items-center justify-between text-[10px] font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                <span>{node.ip}:{node.port}</span>
                                                <div className="flex items-center gap-3">
                                                    {node.online && (
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" /> {node.latency}ms
                                                        </span>
                                                    )}
                                                    {(node.completedCount || 0) > 0 && (
 <span className="text-green-400">{node.completedCount}</span>
                                                    )}
                                                    {(node.inProgressCount || 0) > 0 && (
                                                        <span className="text-blue-400">⟳{node.inProgressCount}</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Latency Bar */}
                                            {node.online && (
                                                <div className={`mt-3 h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                                    <div
                                                        className="h-full rounded-full transition-all duration-1000"
                                                        style={{
                                                            width: `${Math.min(100, node.latency / 2)}%`,
                                                            background: SERVICE_COLORS[node.service] || '#58a6ff',
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* Expanded Detail */}
                                            <AnimatePresence>
                                                {isExpanded && dev && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: 'auto', opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className={`mt-4 pt-3 border-t space-y-2 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                                            {/* Agent Types */}
                                                            {dev.agentTypes.length > 0 && (
                                                                <div>
                                                                    <div className={`text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>에이전트 유형</div>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {dev.agentTypes.map(at => (
                                                                            <span key={at} className={`text-[10px] px-2 py-0.5 rounded border ${isDark ? 'border-white/10 text-blue-300' : 'border-blue-200 text-blue-600 bg-blue-50'}`}>
                                                                                {at}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Completed */}
                                                            {dev.development.completedFeatures.length > 0 && (
                                                                <div>
 <div className={`text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-green-400/70' : 'text-green-600'}`}><CheckCircle2 className="w-4 h-4 inline-block" />완료 ({dev.development.completedFeatures.length})</div>
                                                                    <ul className="space-y-0.5">
                                                                        {dev.development.completedFeatures.map((f, fi) => (
                                                                            <li key={fi} className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>• {f}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}

                                                            {/* In Progress */}
                                                            {dev.development.inProgress.length > 0 && (
                                                                <div>
 <div className={`text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-blue-400/70' : 'text-blue-600'}`}> 진행중 ({dev.development.inProgress.length})</div>
                                                                    <ul className="space-y-0.5">
                                                                        {dev.development.inProgress.map((f, fi) => (
                                                                            <li key={fi} className={`text-[10px] ${isDark ? 'text-blue-300/80' : 'text-blue-500'}`}>⟳ {f}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}

                                                            {/* Scripts */}
                                                            {dev.development.scripts.length > 0 && (
                                                                <div>
 <div className={`text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}><Folder className="w-4 h-4 inline-block" />스크립트</div>
                                                                    {dev.development.scripts.map((s, si) => (
                                                                        <div key={si} className={`text-[10px] font-mono px-2 py-1 rounded ${isDark ? 'bg-black/30 text-gray-400 border border-white/5' : 'bg-gray-50 text-gray-500 border border-gray-100'}`}>
                                                                            {s}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {node.memory && (
                                                                <div className={`flex gap-3 text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                                    <span><Cpu className="w-3 h-3 inline" /> Heap: {node.memory.heapUsed}</span>
                                                                    <span>RSS: {node.memory.rss}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}

                    {/* ═══ Dev Status Tab ═══ */}
                    {activeTab === 'dev' && devData && (
                        <motion.div key="dev" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <div className="flex items-center gap-3 mb-6">
                                <GitBranch className="w-5 h-5 text-blue-400" />
                                <h2 className="text-xl font-bold">에이전트별 개발 진행 현황</h2>
                            </div>

                            {/* Phase Summary Bar */}
                            <div className={`p-4 rounded-2xl border mb-6 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                <div className="flex items-center gap-4 mb-3">
                                    <span className="text-sm font-bold">전체 진행률</span>
                                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {devData.summary.totalFeatures} 기능 완료 · {devData.summary.totalInProgress} 작업 진행중
                                    </span>
                                </div>
                                <div className={`h-3 rounded-full overflow-hidden flex ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                    <div className="h-full bg-green-500 transition-all" style={{ width: `${(devData.summary.production / 9) * 100}%` }} />
                                    <div className="h-full bg-blue-500 transition-all" style={{ width: `${(devData.summary.development / 9) * 100}%` }} />
                                    <div className="h-full bg-gray-400 transition-all" style={{ width: `${(devData.summary.planned / 9) * 100}%` }} />
                                </div>
                                <div className="flex gap-4 mt-2">
 <span className="text-[10px] text-green-400 font-bold"><CircleCheck className="w-4 h-4 inline-block" />운영 {devData.summary.production}</span>
 <span className="text-[10px] text-blue-400 font-bold"> 개발 {devData.summary.development}</span>
                                    <span className={`text-[10px] font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>⬜ 계획 {devData.summary.planned}</span>
                                </div>
                            </div>

                            {/* Dev Cards */}
                            <div className="space-y-3">
                                {devData.nodes.map((node, i) => {
                                    const phase = PHASE_CONFIG[node.development.phase] || PHASE_CONFIG.unknown;
                                    return (
                                        <motion.div
                                            key={node.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.05 }}
                                            className={`p-5 rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <span className="text-3xl mt-1">{node.emoji}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h3 className="text-lg font-bold">{node.name}</h3>
                                                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${phase.bg}`} style={{ color: phase.color }}>
                                                            {phase.label}
                                                        </span>
                                                    </div>
                                                    <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{node.role}</p>

                                                    {/* Skills Row */}
                                                    <div className="flex flex-wrap gap-1.5 mb-3">
                                                        {node.skills.map(s => (
                                                            <span key={s} className="text-[10px] font-medium px-2 py-0.5 rounded" style={{ background: `${SKILL_COLORS[s] || '#6e7681'}15`, color: SKILL_COLORS[s] || '#6e7681' }}>
                                                                {s}
                                                            </span>
                                                        ))}
                                                    </div>

                                                    {/* Agent Types */}
                                                    {node.agentTypes.length > 0 && (
                                                        <div className="flex flex-wrap gap-1.5 mb-3">
                                                            {node.agentTypes.map(at => (
                                                                <span key={at} className={`text-[10px] font-bold px-2 py-0.5 rounded border ${isDark ? 'border-white/10 text-cyan-300 bg-cyan-500/5' : 'border-cyan-200 text-cyan-700 bg-cyan-50'}`}>
 <Bot className="w-4 h-4 inline-block" />{at}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Features Grid */}
                                                    <div className="grid md:grid-cols-2 gap-3">
                                                        {node.development.completedFeatures.length > 0 && (
                                                            <div className={`p-3 rounded-xl ${isDark ? 'bg-green-500/5 border border-green-500/10' : 'bg-green-50 border border-green-100'}`}>
 <div className="text-[10px] font-bold text-green-500 mb-2"><CheckCircle2 className="w-4 h-4 inline-block" />완료 기능 ({node.development.completedFeatures.length})</div>
                                                                <ul className="space-y-1">
                                                                    {node.development.completedFeatures.map((f, fi) => (
                                                                        <li key={fi} className={`text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>• {f}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {node.development.inProgress.length > 0 && (
                                                            <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-500/5 border border-blue-500/10' : 'bg-blue-50 border border-blue-100'}`}>
 <div className="text-[10px] font-bold text-blue-500 mb-2"> 진행중 ({node.development.inProgress.length})</div>
                                                                <ul className="space-y-1">
                                                                    {node.development.inProgress.map((f, fi) => (
                                                                        <li key={fi} className={`text-[11px] ${isDark ? 'text-blue-200' : 'text-blue-600'}`}>⟳ {f}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Scripts */}
                                                    {node.development.scripts.length > 0 && (
                                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                                            {node.development.scripts.map((s, si) => (
                                                                <span key={si} className={`text-[10px] font-mono px-2 py-0.5 rounded ${isDark ? 'bg-black/30 text-gray-500 border border-white/5' : 'bg-gray-50 text-gray-400 border border-gray-100'}`}>
 <FileText className="w-4 h-4 inline-block" />{s}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}

                    {/* ═══ Routing Tab ═══ */}
                    {activeTab === 'routing' && (
                        <motion.div key="routing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <div className="flex items-center gap-3 mb-6">
                                <Layers className="w-5 h-5 text-purple-400" />
                                <h2 className="text-xl font-bold">작업 라우팅 & 페일오버 정책</h2>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Routing Rules */}
                                <div className={`p-5 rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                                        <BrainCircuit className="w-4 h-4 text-purple-400" /> 스킬 기반 라우팅 규칙 (13개)
                                    </h3>
                                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                                        {[
 { pattern: 'cad|dwg|dxf|도면', node: ' Tony', agent: 'cad-agent' },
 { pattern: 'ppt|프레젠|슬라이드', node: ' Tony', agent: 'ppt-agent' },
 { pattern: 'skp|sketchup|3d', node: ' Tony', agent: 'skp-agent' },
 { pattern: 'btl|입찰|안내서', node: '‍ Jessi', agent: 'btl-agent' },
 { pattern: 'code|코드|개발', node: '‍ Jessi', agent: 'code-agent' },
 { pattern: 'photoshop|포토샵', node: '‍ Jessi', agent: 'design-tool' },
 { pattern: 'render|렌더|comfyui', node: ' Roony', agent: 'ren-agent' },
 { pattern: 'concept|컨셉|color', node: ' Rudy', agent: 'design-agent' },
 { pattern: 'vision|비전|diff', node: ' Benny', agent: 'vision-agent' },
 { pattern: 'report|보고서|분석', node: ' Benny', agent: 'report-agent' },
 { pattern: 'resource|백업', node: ' Dony', agent: 'resource-agent' },
 { pattern: 'rag|search|검색', node: ' Poby', agent: 'rag-chat' },
 { pattern: 'schedule|배정', node: ' Shon', agent: 'orchestrator' },
                                        ].map((rule, i) => (
                                            <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                                                <code className={`text-[10px] px-2 py-1 rounded flex-1 ${isDark ? 'bg-purple-500/10 text-purple-300' : 'bg-purple-50 text-purple-600'}`}>
                                                    {rule.pattern}
                                                </code>
                                                <ArrowRight className={`w-3 h-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                                                <span className="text-xs font-bold min-w-[80px]">{rule.node}</span>
                                                <span className={`text-[10px] px-2 py-0.5 rounded ${isDark ? 'bg-cyan-500/10 text-cyan-300' : 'bg-cyan-50 text-cyan-600'}`}>
                                                    {rule.agent}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Failover Policy */}
                                <div className={`p-5 rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                                        <ShieldCheck className="w-4 h-4 text-green-400" /> 페일오버 & 운영 정책
                                    </h3>

                                    <div className="space-y-4">
                                        <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                                            <div className={`text-[10px] font-bold uppercase mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Gateway 우선순위</div>
                                            <div className="flex items-center gap-2 flex-wrap">
 {[' Poby (Primary)', ' Rudy (Backup)'].map((g, i) =>(
                                                    <span key={i} className={`text-xs font-bold px-3 py-1.5 rounded-lg ${i === 0
                                                        ? isDark ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-green-50 text-green-600 border border-green-200'
                                                        : isDark ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' : 'bg-yellow-50 text-yellow-600 border border-yellow-200'
                                                    }`}>
                                                        {g}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                                            <div className={`text-[10px] font-bold uppercase mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>헬스체크 설정</div>
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { k: '주기', v: '5초' },
                                                    { k: '타임아웃', v: '4초' },
                                                    { k: '재시도', v: '3회' },
                                                    { k: '재시도 딜레이', v: '2초' },
                                                ].map(p => (
                                                    <div key={p.k} className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        <span className="font-bold">{p.k}:</span> <span className="font-mono">{p.v}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                                            <div className={`text-[10px] font-bold uppercase mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>노드 우선순위 체인</div>
                                            <div className="flex flex-wrap items-center gap-1">
 {[' Poby', ' Rudy', '‍ Jessi', ' Dony', ' Benny', ' Shon', ' Tony', ' Roony', ' Meety'].map((n, i) =>(
                                                    <div key={i} className="flex items-center gap-1">
                                                        <span className={`text-[10px] font-bold px-2 py-1 rounded ${isDark ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                                            #{i + 1} {n}
                                                        </span>
                                                        {i < 8 && <ArrowRight className={`w-3 h-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* NAS Watcher */}
                                        <div className={`p-4 rounded-xl ${isDark ? 'bg-blue-500/5 border border-blue-500/10' : 'bg-blue-50 border border-blue-100'}`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <FolderSync className="w-4 h-4 text-blue-400" />
                                                <span className="text-xs font-bold text-blue-400">NAS Watcher 상태</span>
                                                {nasInfo?.watcherStatus === 'active' ? (
                                                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                                                ) : (
                                                    <AlertTriangle className="w-3 h-3 text-yellow-400" />
                                                )}
                                            </div>
                                            <div className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                감시 경로: <code className="font-mono">SSH → /volume1/insight drive/B_DESIGN</code><br />
                                                이벤트 수: {nasInfo?.recentEvents?.length || 0}건
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* ═══ NAS Watcher Tab (T19-2) ═══ */}
                    {activeTab === 'watcher' && (
                        <motion.div key="watcher" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <Eye className="w-5 h-5 text-cyan-400" />
                                    <h2 className="text-xl font-bold">NAS File Watcher</h2>
                                    {watcherStatus?.running && (
                                        <span className="flex items-center gap-1.5 text-xs bg-green-500/10 text-green-400 px-3 py-1 rounded-full font-bold">
                                            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />LIVE
                                        </span>
                                    )}
                                </div>
                                <button onClick={() => { setLoading(true); fetchData(); }} className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />{lastUpdate}
                                </button>
                            </div>

                            {/* Watcher Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                                {[
                                    { label: '감시 폴더', val: watcherStatus?.dirs || 0, icon: <FolderOpen className="w-4 h-4" />, color: '#3b82f6' },
                                    { label: '추적 파일', val: watcherStatus?.files || 0, icon: <FileSearch className="w-4 h-4" />, color: '#22c55e' },
                                    { label: '감지 이벤트', val: watcherStatus?.events || 0, icon: <Zap className="w-4 h-4" />, color: '#f59e0b' },
                                    { label: '스캔 횟수', val: watcherStatus?.scanCount || 0, icon: <Timer className="w-4 h-4" />, color: '#8b5cf6' },
                                    { label: '주기', val: `${(watcherStatus?.interval || 5000) / 1000}초`, icon: <Clock className="w-4 h-4" />, color: '#06b6d4' },
                                ].map(s => (
                                    <div key={s.label} className={`p-3 rounded-xl border text-center ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <div className="flex items-center justify-center gap-1.5 mb-1" style={{ color: s.color }}>{s.icon}</div>
                                        <div className="text-xl font-bold font-mono" style={{ color: s.color }}>{s.val}</div>
                                        <div className={`text-[10px] font-semibold uppercase ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid md:grid-cols-3 gap-6">
                                {/* File Events Feed */}
                                <div className="md:col-span-2">
                                    <div className="flex items-center justify-between mb-3">
 <h3 className="text-sm font-bold"><ClipboardList className="w-4 h-4 inline-block" />파일 변경 이벤트</h3>
                                        <div className="flex gap-1">
                                            {['all', 'created', 'modified', 'deleted'].map(f => (
                                                <button key={f} onClick={() => setEventFilter(f)}
                                                    className={`text-[10px] px-2.5 py-1 rounded-full font-bold transition-colors ${
                                                        eventFilter === f
                                                            ? 'bg-cyan-500/20 text-cyan-300'
                                                            : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                                >
 {f === 'all' ? '전체' : f === 'created' ? '생성' : f === 'modified' ? '수정' : '삭제'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        {fileEvents.length === 0 ? (
                                            <div className={`p-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                                <p className="text-sm">아직 감지된 변경 이벤트가 없습니다</p>
                                                <p className="text-xs mt-1">NAS 파일이 변경되면 여기에 표시됩니다</p>
                                            </div>
                                        ) : (
                                            <div className="max-h-[500px] overflow-y-auto divide-y divide-white/5">
                                                {fileEvents
                                                    .filter(e => eventFilter === 'all' || e.type === eventFilter)
                                                    .map((ev, i) => (
                                                    <div key={i} className={`px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors`}>
                                                        {EVENT_ICONS[ev.type]}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-medium truncate">{ev.name}</span>
                                                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{
                                                                    background: `${EXT_COLORS[ev.ext] || '#6b7280'}15`,
                                                                    color: EXT_COLORS[ev.ext] || '#6b7280',
                                                                }}>{ev.ext}</span>
                                                            </div>
                                                            <p className={`text-[10px] truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{ev.path}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className={`text-[10px] font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                                {ev.size ? `${(ev.size / 1024).toFixed(0)}KB` : ''}
                                                            </div>
                                                            <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                                {new Date(ev.timestamp).toLocaleTimeString('ko-KR')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Watcher Config Panel */}
                                <div className="space-y-4">
                                    {/* Connection Status */}
                                    <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
                                            <HardDrive className="w-4 h-4 text-cyan-400" /> NAS 연결 상태
                                        </h3>
                                        <div className="space-y-2">
                                            <div className={`flex items-center justify-between p-2.5 rounded-lg ${watcherStatus?.running ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                                <span className="text-xs">Gateway</span>
                                                <span className={`text-xs font-bold ${watcherStatus?.running ? 'text-green-400' : 'text-red-400'}`}>
                                                    {watcherStatus?.running ? '● 연결됨' : '○ 오프라인'}
                                                </span>
                                            </div>
                                            <div className={`flex items-center justify-between p-2.5 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                                                <span className="text-xs">NAS IP</span>
                                                <span className="text-xs font-mono">124.55.60.40 (SSH)</span>
                                            </div>
                                            <div className={`flex items-center justify-between p-2.5 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                                                <span className="text-xs">마지막 스캔</span>
                                                <span className="text-xs font-mono">
                                                    {watcherStatus?.lastScan ? new Date(watcherStatus.lastScan).toLocaleTimeString('ko-KR') : '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Monitored Dirs */}
                                    <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
                                            <FolderSync className="w-4 h-4 text-blue-400" /> 감시 디렉토리 ({watcherConfig?.dirs?.length || 0})
                                        </h3>
                                        <div className="max-h-[200px] overflow-y-auto space-y-1">
                                            {watcherConfig?.dirs?.map((dir, i) => (
                                                <div key={i} className={`text-[10px] font-mono p-1.5 rounded ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
 <FolderOpen className="w-4 h-4 inline-block" />{dir}
                                                </div>
                                            )) || <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>로딩 중...</p>}
                                        </div>
                                    </div>

                                    {/* Watched Extensions */}
                                    <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
                                            <FileSearch className="w-4 h-4 text-purple-400" /> 감시 확장자
                                        </h3>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(watcherConfig?.extensions || ['.dwg', '.xlsx', '.pdf', '.skp', '.jpg', '.png']).map(ext => (
                                                <span key={ext} className="text-[10px] font-bold px-2 py-1 rounded" style={{
                                                    background: `${EXT_COLORS[ext] || '#6b7280'}15`,
                                                    color: EXT_COLORS[ext] || '#6b7280',
                                                }}>{ext}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </section>

            {/* DSLM Link */}
            <section className="max-w-5xl mx-auto px-6 pb-16">
                <div className={`p-6 rounded-2xl border flex flex-col md:flex-row items-center gap-6 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
 <div className="text-4xl"><Brain className="w-4 h-4 inline-block" /></div>
                    <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1">DSLM 엔진 (Poby)</h3>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Mac Studio M4 Max 64GB에서 실행 중인 도메인 특화 AI —
                            Qwen2.5:32b + Qwen2.5-VL + bge-m3 임베딩
                        </p>
                    </div>
                    <Link
                        href="/dslm"
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 rounded-xl font-semibold transition-all flex items-center gap-2 text-sm text-white"
                    >
                        DSLM 상세 <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className={`py-8 border-t text-center text-xs ${isDark ? 'border-white/5 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                © 2026 Desinsight. SnapGRID × SnapCodex | 특허 제10-2890450호
            </footer>
        </main>
    );
}
